/**
 * Client switcher for the topbar.
 *
 * An FPS Super Admin works inside one client at a time, exactly like a client
 * admin does — the platform level is a separate surface, not a mode where
 * queries stop being scoped. This picks which workspace you are in.
 *
 * Shown only when there is a choice to make: a client admin assigned to one
 * organization sees their name as plain text rather than a dropdown that
 * cannot do anything.
 */

import { T } from '../lib/copyEdit.tsx';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export interface ClientOption {
  id: string;
  name: string;
  primaryColor: string;
  /** The icon fetched from the client's domain, as a data URI. Null until
   *  someone runs Fetch brand on the client form. */
  appIconData?: string | null;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * The client's mark: their own icon where we have one, initials otherwise.
 *
 * DELIBERATELY THE FETCHED ICON RATHER THAN THE UPLOADED LOGO. The uploaded
 * logo is the client's brand as their SPONSORS see it, and it leads the portal
 * and every sponsor email; that is its job. This is a 26px square in a
 * switcher, where a wide horizontal lockup would shrink to an illegible smear.
 *
 * The icon is a data URI on the client record rather than a remote URL, so it
 * cannot phone home to a third party on every page load and cannot break when
 * someone else's favicon service goes down.
 *
 * Initials are not a placeholder to be embarrassed about — a client added
 * without a domain will never have an icon, and two letters on their brand
 * colour is a perfectly good identifier.
 */
function ClientMark({ client, fallback }: { client?: ClientOption; fallback?: string }) {
  const label = fallback ?? initials(client?.name ?? '?');

  if (client?.appIconData) {
    return (
      <span className="sw sw--icon">
        <img src={client.appIconData} alt="" />
      </span>
    );
  }

  return (
    <span className="sw" style={{ background: client?.primaryColor ?? 'var(--gray-400)' }}>
      {label}
    </span>
  );
}

export default function ClientSwitcher({
  clients,
  currentId,
  showAllOption,
}: {
  clients: ClientOption[];
  currentId: string;
  showAllOption: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    function onEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  const current = clients.find((c) => c.id === currentId);
  const hasChoice = clients.length > 1 || showAllOption;

  if (!hasChoice) {
    return (
      <span className="switch-static">
        <ClientMark client={current} />
        <b>{current?.name ?? 'Workspace'}</b>
      </span>
    );
  }

  return (
    <div className={`dd${open ? ' open' : ''}`} ref={ref}>
      <button type="button" className="switch-btn" onClick={() => setOpen((v) => !v)}>
        <ClientMark client={current} />
        <b>{current?.name ?? 'Select a client'}</b>
        <svg className="icon icon-sm" viewBox="0 0 24 24" aria-hidden="true">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      <div className="dd-menu">
        {showAllOption && (
          <>
            <div className="dd-label"><T k="switcher.group.platform" d="Platform" /></div>
            <button
              type="button"
              className="dd-item"
              onClick={() => {
                setOpen(false);
                void navigate('/');
              }}
            >
              <span className="sw" style={{ background: 'var(--gray-400)' }}>ALL</span>
              All Clients</button>
          </>
        )}
        <div className="dd-label"><T k="switcher.group.clients" d="Clients" /></div>
        {clients.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`dd-item${c.id === currentId ? ' on' : ''}`}
            onClick={() => {
              setOpen(false);
              void navigate(`/c/${c.id}`);
            }}
          >
            <ClientMark client={c} />
            {c.name}
          </button>
        ))}
      </div>
    </div>
  );
}
